import { Injectable } from '@angular/core';
import { DocumentNode } from 'graphql';
import gql from 'graphql-tag';

@Injectable({
  providedIn: 'root'
})
export class Queries {
  getPaginatedMaintenances(): DocumentNode {
    return gql`
      query getPaginatedMaintenances($where: MaintenanceWhereInput, $orderBy: MaintenanceOrderByInput, $limit: Int, $offset:Int) {
        maintenances(where: $where, orderBy: $orderBy, limit: $limit, offset: $offset) {
          items {
            id
            number
            labels {
              name
              color
            }
            company {
              id
              name
            }
            location {
              name
              address {
                formattedAddress
              }
            }
            segment {
              name
            }
            maintenanceType {
              name
            }
            scheduled {
              date
              time
            }
            warrantyStatus
            status
            openedAt
            updatedAt
            closedAt
            deadlineAt
            responseDeadlineStatus
            solutionDeadlineStatus
            firstVisitStartedAt
            firstVisitDeadlineAt
            archived
            ratingAverage
            visitCount
            statusDescription
            message
            totalValue
            productsTotalValue
            servicesTotalValue
          }
          totalCount
        }
      }`;
  }

  getPaginatedMaintenancesWithWarranties(): DocumentNode {
    return gql`
      query getPaginatedMaintenancesWithWarranties($where: MaintenanceWhereInput, $orderBy: MaintenanceOrderByInput, $limit: Int, $offset:Int) {
        maintenances(where: $where, orderBy: $orderBy, limit: $limit, offset: $offset) {
          items {
            id
            number
            labels {
              name
              color
            }
            company {
              id
              name
            }
            location {
              name
            }
            segment {
              name
            }
            maintenanceType {
              name
            }
            scheduled {
              date
              time
            }
            weight
            warrantyStatus
            status
            openedAt
            updatedAt
            closedAt
            deadlineAt
            responseDeadlineStatus
            solutionDeadlineStatus
            firstVisitStartedAt
            firstVisitDeadlineAt
            archived
            ratingAverage
            visitCount
            statusDescription
            message
            totalValue
            productsTotalValue
            servicesTotalValue
            warrantySummary {
              status
              startDate
              endDate
              extendedEndDate
              count {
                equipment
                maintenance
                total
              }
            }
            warranties {
              id
            },
            pendencies (take: 50) {
              items {
                id
                title
                archived
                status
                treatmentStatus
                pendencyType {
                  name
                }
              }
            }
            payment {
              id
              number
              status
            }
          }
          totalCount
        }
      }`;
  }

  getMaintenance(): DocumentNode {
    return gql`
      query getMaintenance($id: ID!) {
        maintenance(id: $id) {
          id
          archived
          weight
          scheduled {
            date
            time
          }
          scheduleSuggestionsStatus
          scheduleSuggestions {
            date
            time
          }
          maintenanceType {
            id
            name
            type
            archived
          }
          segment {
            id
            name
            archived
            maintenanceTypes {
              id
            }
          }
          problem {
            id
            name
          }
          location {
            id
            name
            address {
              formattedAddress
              coords {
                latitude
                longitude
              }
            }
          }
          locationSectors {
            id
            name
            description
            archived
          }
          locationEnvironments {
            id
            name
            description
            archived
            locationSector {
              id
              name
            }
          }
          address {
            zipCode
            state
            city
            number
            street
            neighborhood
            complement
            coords {
              latitude
              longitude
            }
          }
          number
          requester {
            id
          }
          labels {
            id
            name
            color
          }
          company {
            id
            fieldAccountId
            name
            avatarUrl
          }
          assignee {
            id
            name
            archived
          }
          equipments {
            id
            name
            number
            notes
            imageUrl
            archived
            createdAt
            type {
              id
              name
            }
            locationSector {
              id
              name
            }
            locationEnvironment {
              id
              name
            }
          }
          attachments {
            id
            path
            title
            type
            createdAt
          }
          comments(orderBy: createdAt_desc, limit: 20, offset: 0) {
            id
            message
            createdAt
            archived
            type
            company {
              id
              name
            }
            postedBy {
              id
              name
              avatarUrl
              type
            }
          }
          logs(orderBy: createdAt_asc) {
            clientApplicationName
            clientSource
            number
            message
            archived
            status
            statusDescription
            scheduleSuggestionsStatus
            scheduleSuggestions {
              date
              time
            }
            scheduled {
              date
              time
            }
            segment {
              id
              name
            }
            location {
              id
              name
              address {
                formattedAddress
                coords {
                  latitude
                  longitude
                }
              }
            }
            rating {
              stars
              comments
              createdAt
            }
            company {
              id
              name
            }
            assignee {
              id
              name
              avatarUrl
            }
            requester {
              id
              name
              avatarUrl
              email
            }
            updatedAt
            closedAt
            deadlineAt
            maintenanceType {
              id
              name
            }
            createdAt
            updatedBy {
              id
              name
              type
            }
          }
          openedAt
          closedAt
          deadlineAt
          firstVisitStartedAt
          firstVisitDeadlineAt
          totalValue
          productsTotalValue
          servicesTotalValue
          archived
          updatedBy {
            id
            name
          }
          status
          message
          statusDescription
          soUrl
          tasks {
            id
            status
            startedAt
            completedAt
            reportedReason
            description
            latitude
            longitude
            position
            timezone
            statusDescription
            scheduled {
              date
              time
            }
            archived
            createdAt
            receivedAt
            viewedAt
            startedRouteAt
            employee {
              name
              avatar
            }
            rating {
              stars
              comments
            }
            logs(orderBy: createdAt_asc) {
              id
              updatedBy {
                id
                name
                type
                source
                offline
                offlineClock
                latitude
                longitude
              }
              createdAt
              snapshot {
                id
                company {
                  name
                }
                scheduled {
                  date
                  time
                }
                startedAt
                completedAt
                status
                statusDescription
                latitude
                longitude
                archived
                receivedAt
                viewedAt
                employee {
                  name
                }
                rating {
                  stars
                  comments
                }
              }
            }
          }
          formAnswers {
            id
            name
            archived
            createdAt
            equipment {
              id
              name
              number
              type {
                name
              }
            }
            questionAnswers {
              id
              title
              answer
              type
              required
              conditions {
                operator
                value
                question {
                  id
                }
              }
              options {
                value
              }
            }
          }
          quotations {
            id
          }
          scheduleSuggestions {
            date
            time
          }
          companyScheduleSuggestion {
            date
            time
          }
          responseDeadlineStatus
          solutionDeadlineStatus
          deadlineContract {
            name
            considerHoliday
            hasCustomInterval
            responseRisk {
              valueInMinutes
              unity
            }
            solutionRisk {
              valueInMinutes
              unity
            }
            responseDeadline {
              valueInMinutes
              unity
            }
            solutionDeadline {
              valueInMinutes
              unity
            }
            originalContract {
              id
              name
            }
            responseDeadlineViolationReason {
              deadlineViolationReason {
                id
                name
              }
              linkedBy
            }
            solutionDeadlineViolationReason {
              deadlineViolationReason {
                id
                name
              }
              linkedBy
            }
            customInterval {
              monday {
                start
                end
              }
              tuesday {
                start
                end
              }
              wednesday {
                start
                end
              }
              thursday {
                start
                end
              }
              friday {
                start
                end
              }
              saturday {
                start
                end
              }
              sunday {
                start
                end
              }
            }
          }
          previousMaintenance {
            id
            number
          }
        }
      }
    `;
  }

  getComments(): DocumentNode {
    return gql`
      query getMaintenance($maintenanceId: ID!, $limit: Int, $offset:Int) {
        maintenance(id: $maintenanceId) {
          comments(orderBy: createdAt_desc, limit: $limit, offset: $offset) {
            id
            message
            createdAt
            archived
            type
            postedBy {
              id
              name
              avatarUrl
              type
            }
            company {
              id
              name
            }
          }
        }
      }
    `;
  }

  getProductsServices(): DocumentNode {
    return gql`
      query getMaintenance(
        $maintenanceId: ID!
        $orderBy: MaintenanceProductServiceOrderByInput
        $where: MaintenanceProductServiceWhereInput
        $limit: Int
        $offset:Int
      ) {
        maintenance(id: $maintenanceId) {
          productsServices(
            orderBy: $orderBy
            where: $where
            limit: $limit
            offset: $offset
          ) {
            totalCount
            totalValue
            items {
              value
              quantity
              storage {
                id
                name
              }
              item {
                name
                measure
                imageUrl
                type
                productServiceCompanies {
                  preferences {
                    weekDayValues {
                      monday
                      tuesday
                      wednesday
                      thursday
                      friday
                      saturday
                      sunday
                    }
                  }
                  valueType
                }
              }
            }
            pageInfo {
              hasNextPage
              hasPreviousPage
            }
          }
        }
      }
    `;
  }

  getQuotations(): DocumentNode {
    return gql`
      query getMaintenance($maintenanceId: ID!) {
        maintenance(id: $maintenanceId) {
          quotations {
            id
            identifier
            name
            subTotal
            total
            status
            discount {
              total
            },
            items {
              quantity
              total
              productServiceValue
              productService {
                type
              }
            }
          }
        }
      }
    `;
  }

  getPendenciesTotalCount(): DocumentNode {
    return gql`
    query getMaintenance($id: ID!) {
      maintenance(id: $id) {
        pendencies(take: 1, after: null) {
          totalCount
        }
      }
    }
  `;
  }
}
